// AS1a: demomarken "Demojakt – Qvenari" i Min jakt. API:t skickar med den i myMarks för den som
// saknar egen mark och avvisar alla ändringar (services/demoMark.ts). Här: banner på markens sida,
// spåren från den avslutade jakten och knappen som visar den igen.
import React, { useEffect, useMemo, useState } from 'react';
import { Eye, EyeOff, Info, Route } from 'lucide-react';
import { api, type HuntTrack, type Mark } from '../../api';
import { RADIUS, TYPE, useTheme } from '../../theme';
import { Button, Icon, Sheet } from '../../components/ui';

export const DEMO_MARK_ID = 'demo-mark';
/** Mötes-id för demomarkens avslutade jakt (spåren sparas bara där). */
export const DEMO_DONE_MEETING = 'demo-hunt-done';

export function isDemoMark(m: Pick<Mark, 'id' | 'isDemo'> | null | undefined): boolean {
  return !!m && (m.isDemo === true || m.id === DEMO_MARK_ID);
}

/** Överst på demomarkens sida: vad det är, spåren och Dölj. */
export function DemoMarkBanner({ onHidden }: { onHidden: () => void }) {
  const T = useTheme();
  const [busy, setBusy] = useState(false);
  const [showTracks, setShowTracks] = useState(false);
  const hide = async () => {
    setBusy(true);
    try { await api.setDemoMarkHidden(true); onHidden(); }
    catch (e) { alert((e as Error).message || 'Kunde inte dölja demomarken'); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ maxWidth: 1264, margin: '12px auto 0', padding: '0 clamp(16px,3vw,32px)' }}>
      <div role="note" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, background: T.infoSoft, color: T.text, borderRadius: RADIUS.lg, padding: '12px 14px' }}>
        <Icon as={Info} color={T.info} />
        <div style={{ flex: '1 1 260px', minWidth: 0 }}>
          <div style={{ ...TYPE.title, fontSize: 15 }}>Det här är en demomark</div>
          <div style={{ ...TYPE.small, color: T.textSub, marginTop: 2 }}>
            Klicka runt i karta, kalender, chatt och album för att se hur Qvenari fungerar. Inget här går att ändra. Demomarken försvinner när du har en egen jaktmark.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button size="sm" variant="secondary" icon={<Icon as={Route} />} onClick={() => setShowTracks(true)}>Spår från senaste jakten</Button>
          <Button size="sm" variant="ghost" icon={<Icon as={EyeOff} />} loading={busy} onClick={hide}>Dölj demomarken</Button>
        </div>
      </div>
      <DemoHuntTracks open={showTracks} onClose={() => setShowTracks(false)} />
    </div>
  );
}

type LatLng = [number, number];
const TRACK_COLORS = ['#e5882a', '#2b6cb0', '#7a4fb0', '#2f855a', '#c53030'];

/** Ritar gränser, såter, pass, fällt vilt och spåren (vanliga spår finns bara under pågående jakt). */
export function DemoHuntTracks({ open, onClose }: { open: boolean; onClose: () => void }) {
  const T = useTheme();
  const [tracks, setTracks] = useState<HuntTrack[] | null>(null);
  const [pins, setPins] = useState<{ type: string; lat: number; lng: number; name?: string }[]>([]);
  const [mark, setMark] = useState<Mark | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    if (!open || tracks) return;
    Promise.all([api.huntTracks(DEMO_DONE_MEETING, 0), api.markPins(DEMO_MARK_ID), api.myMarks()])
      .then(([t, p, ms]) => { setTracks(t); setPins(p); setMark(ms.find(isDemoMark) ?? null); })
      .catch(e => setErr((e as Error).message || 'Kunde inte hämta spåren'));
  }, [open, tracks]);

  const areas = useMemo(() => {
    try {
      const fc = JSON.parse(mark?.coordinatesJson || '{}');
      return (fc.features || []).map((f: any) => ({ sub: f.properties?.type === 'sub_area', name: f.properties?.name as string, ring: f.geometry.coordinates[0].map(([lng, lat]: number[]) => [lat, lng] as LatLng) }));
    } catch { return []; }
  }, [mark?.coordinatesJson]) as { sub: boolean; name: string; ring: LatLng[] }[];

  const W = 600, H = 420, PAD = 16;
  const proj = useMemo(() => {
    const all: LatLng[] = [...areas.flatMap(a => a.ring), ...(tracks ?? []).flatMap(t => t.points.map(p => [p[0], p[1]] as LatLng))];
    if (!all.length) return null;
    const lats = all.map(p => p[0]), lngs = all.map(p => p[1]);
    const minLat = Math.min(...lats), maxLat = Math.max(...lats), minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
    const kx = Math.cos(((minLat + maxLat) / 2) * Math.PI / 180);
    const s = Math.min((W - 2 * PAD) / ((maxLng - minLng) * kx || 1), (H - 2 * PAD) / ((maxLat - minLat) || 1));
    return ([lat, lng]: LatLng) => [PAD + (lng - minLng) * kx * s, H - PAD - (lat - minLat) * s] as [number, number];
  }, [areas, tracks]);
  const path = (pts: LatLng[]) => proj ? pts.map((p, i) => `${i ? 'L' : 'M'}${proj(p).map(n => n.toFixed(1)).join(' ')}`).join('') : '';
  const centroid = (ring: LatLng[]) => [ring.reduce((a, p) => a + p[0], 0) / ring.length, ring.reduce((a, p) => a + p[1], 0) / ring.length] as LatLng;

  const fmt = (ms: number) => new Date(ms).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
  return (
    <Sheet open={open} onClose={onClose} title="Spår från senaste jakten" maxWidth={680}>
      {err && <div style={{ color: T.danger }}>{err}</div>}
      {!err && !proj && <div style={{ color: T.textMuted }}>Hämtar spåren …</div>}
      {proj && (
        <>
          <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Karta över demomarken med jägarnas och hundens spår" style={{ width: '100%', height: 'auto', background: T.map, borderRadius: RADIUS.md, display: 'block' }}>
            {areas.map((a, i) => <path key={i} d={path(a.ring)} fill={a.sub ? 'none' : 'rgba(78,89,76,0.25)'} stroke={a.sub ? '#00A2FF' : '#0064FF'} strokeWidth={a.sub ? 1.5 : 2.5} strokeDasharray={a.sub ? '6 4' : undefined} />)}
            {areas.filter(a => a.sub).map((a, i) => { const [x, y] = proj(centroid(a.ring)); return <text key={i} x={x} y={y} textAnchor="middle" fontSize={13} fill={T.textSub} fontWeight={600}>{a.name}</text>; })}
            {(tracks ?? []).map((t, i) => <path key={t.kind + t.id} d={path(t.points.map(p => [p[0], p[1]]))} fill="none" stroke={TRACK_COLORS[i % TRACK_COLORS.length]} strokeWidth={t.kind === 'dog' ? 2 : 2.5} strokeLinejoin="round" opacity={0.9} />)}
            {pins.filter(p => p.type === 'pass').map((p, i) => { const [x, y] = proj([p.lat, p.lng]); return <g key={i}><circle cx={x} cy={y} r={7} fill="#fff" stroke="#1c2117" /><text x={x} y={y + 3.5} textAnchor="middle" fontSize={9} fontWeight={700} fill="#1c2117">{(p.name || '').match(/\d+/)?.[0] ?? ''}</text></g>; })}
            {pins.filter(p => p.type === 'skjutet_vilt').map((p, i) => { const [x, y] = proj([p.lat, p.lng]); return <g key={i}><circle cx={x} cy={y} r={8} fill="#c53030" stroke="#fff" strokeWidth={2} /><title>{p.name}</title></g>; })}
          </svg>
          <ul style={{ listStyle: 'none', padding: 0, margin: '12px 0 0', display: 'grid', gap: 6 }}>
            {(tracks ?? []).map((t, i) => (
              <li key={t.kind + t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, ...TYPE.small }}>
                <span style={{ width: 18, height: 4, borderRadius: 2, background: TRACK_COLORS[i % TRACK_COLORS.length], flexShrink: 0 }} />
                <span style={{ color: T.text }}>{t.name}{t.kind === 'dog' ? ' (hund)' : ''}</span>
                {t.points.length > 1 && <span style={{ color: T.textMuted }}>{fmt(t.points[0][2])}–{fmt(t.points[t.points.length - 1][2])}</span>}
              </li>
            ))}
            <li style={{ display: 'flex', alignItems: 'center', gap: 8, ...TYPE.small, color: T.textMuted }}>
              <span style={{ width: 10, height: 10, borderRadius: 5, background: '#c53030', flexShrink: 0 }} />Fällt vilt · siffrorna är passen
            </li>
          </ul>
        </>
      )}
    </Sheet>
  );
}

/** Min jakt utan marker: visa demomarken igen efter att den dolts. */
export function ShowDemoMarkButton({ onShown }: { onShown: (marks: Mark[]) => void }) {
  const [busy, setBusy] = useState(false);
  const show = async () => {
    setBusy(true);
    try { await api.setDemoMarkHidden(false); onShown(await api.myMarks()); }
    catch (e) { alert((e as Error).message || 'Kunde inte visa demomarken'); }
    finally { setBusy(false); }
  };
  return <Button variant="ghost" size="sm" pill icon={<Icon as={Eye} />} loading={busy} onClick={show}>Visa demomarken</Button>;
}
